/**
 * @import { Token } from './Token.js'
 * @import { Ok, Error } from '../ts/Responses.ts'
 * @import { JSONValue } from '../ts/Assertions.ts'
 */

/**
 * Storage keys are 1 or more slash-separated components
 * using the characters a-z, A-Z, 0-9, hyphen, colon and period.
 *
 * Examples:
 * - acmeSetting
 * - acme.com/applicant/22fc01-b0d3084870b-fcae49ac2-892668
 * - address/line1
 * - url/acme.com
 *
 * The key needs to work as part of a REST/HTTP pathname.
 */
const KEY_PATTERN = /^[\w\-.:]+(?:\/[\w\-.:]+)*$/

/**
 * As above, but % and _ are both allowed as a wildcards.
 */
const KEYLIKE_PATTERN = /^[\w\-.:%_]+(?:\/[\w\-.:%_]+)*$/

/**
 * An instance of this class is normally obtained from BrowserApp, which constructs
 * the instance using the party token.
 */
export class Storage {
  #token    // Party token which must include getitem and setitem capabilities on party:control.

  /**
   * @param {Token} token to use for storage requests.
   */
  constructor(token) {
    this.#token = token
  }

  /**
   * Gets the item whose key is provided. Keys are in the form
   * 'some[/key]*' without leading or trailing '/'.
   *
   * @param {string} key used to retrieve the item.
   * @return {Promise<Ok<JSONValue> | Error>} ok or error result.
   */
  getItem(key) {
    return getItem(this.#token, key)
  }

  /**
   * Gets the list (possibly empty) of items matching the SQL-like
   * argument.
   *
   * @param {string} keylike a key matcher such as 'myApp/%'.
   * @return {Promise<Ok<JSONValue[]>>} a list of values.
   */
  getItemsLike(keylike) {
    return getItemsLike(this.#token, keylike)
  }

  /**
   * Stores an item with the supplied key and value.
   *
   * @param {string} key the key to use.
   * @param {JSONValue} value to store.
   * @return {Promise<Ok<true> | Error>} ok if successful, error otherwise.
   */
  setItem(key, value) {
    return setItem(this.#token, key, value)
  }

  /**
   * Removes the item with the specified key, if any.
   *
   * @param {string} key the key to remove.
   * @return {Promise<Ok<true> | Error>} ok if successful, error otherwise.
   */
  removeItem(key) {
    return removeItem(this.#token, key)
  }
}

/**
 * Throws an exception if the key being used is not
 * in a valid format.
 *
 * @param {string} key
 */
function assertValid(key) {
  if (key.match(KEY_PATTERN) == null) {
    throw `DaemonStorage: Invalid key: '${key}`
  }
}

/**
 * Throws an exception if the key like pattern being used
 * is not in a valid format.
 *
 * @param {string} keylike
 */
function assertValidLike(keylike) {
  if (keylike.match(KEYLIKE_PATTERN) == null) {
    throw `DaemonStorage: Invalid like key: ${keylike}`
  }
}

/**
 * Returns the value as a JSON string
 *
 * @param {JSONValue} value
 * @return {string} JSON string.
 * @throws {string} exception if the value is not JSON.
 */
function serialise(value) {
  try {
    return JSON.stringify(value)
  }
  catch (_e) {
    throw `DaemonStorage: Invalid value`
  }
}

/**
 * Sets an item in daemon storage.
 *
 * The value must be an object or primitive
 * which is serialised before saving.
 *
 * @param {Token} token to use.
 * @param {string} key the item key.
 * @param {any} value the serialisable value for the item.
 * @return {Promise<Ok<true> | Error>} ok or error object.
 */
export async function setItem(token, key, value) {
  assertValid(key)
  const body = serialise(value)
  const url = `${token.getAud()}/storage/${key}`
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'X-Tabserver-Token': token.asSignedBase64(),
      'Content-Type': 'application/json'
    },
    body
  })
  return response.json()
}

/**
 * Gets an item from daemon storage.
 *
 * Returns an 'ok' object with the value if present, otherwise
 * returns an 'error' object.
 *
 * @param {Token} token to use.
 * @param {string} key the item key to retrieve.
 * @return {Promise<Ok<JSONValue> | Error>} ok object with value, or error.
 */
export async function getItem(token, key) {
  assertValid(key)
  const url = `${token.getAud()}/storage/${key}`
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'X-Tabserver-Token': token.asSignedBase64()
    }
  })
  return response.json()
}

/**
 * Gets zero or more items from daemon storage, whose
 * keys match the keyLike pattern. This is normally
 * something like 'my/prefix/%' which gets all values
 * with that prefix.
 *
 * You can use both '%' and '_' as wildcards for string
 * of any length and single character respectively.
 *
 * @param {Token} token to use.
 * @param {string} keylike the item keylike pattern to match.
 * @return {Promise<Ok<JSONValue[]>>} ok object with list, or error.
 */
export async function getItemsLike(token, keylike) {
  assertValidLike(keylike)
  const url = new URL(`${token.getAud()}/storage`)
  url.searchParams.append('like', keylike)
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'X-Tabserver-Token': token.asSignedBase64()
    }
  })
  return await response.json()
}

/**
 * Removes an item from daemon storage.
 *
 * @param {Token} token to use.
 * @param {string} key the item key to remove.
 * @return {Promise<Ok<true> | Error>} ok or error object.
 */
export async function removeItem(token, key) {
  assertValid(key)
  const url =`${token.getAud()}/storage/${key}`
  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'X-Tabserver-Token': token.asSignedBase64()
    }
  })
  return response.json()
}
